import { transparentize } from 'polished';
import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { media, hover } from '@peloton/styles';
import scrollableRowStyles from '@peloton/styles/scrollableRowStyles';
import type { CategoryLink } from '@engage/browse-categories';
import {
  browseCategorySlugToCaesarMapper,
  BrowseCategorySlug,
} from '@engage/browse-categories';
import { slate1, gray1, white, black, superRedA11y } from '@engage/colors';
import { If } from '@engage/conditional-render';
import { spaces } from '@engage/styles';
import { label12 } from '@engage/typography';
import { useClasses, Classes, useAria, Aria } from '@members/copy';
import { toScheduleCategoryRoute } from '@members/schedule/urls';
import { useIsFreemiumMember } from '@members/tiering/hooks/useIsFreemiumMember';
import { toLibraryCategoryRoute } from '../pg-library/urls';
import { BROWSE_CATEGORY_HEIGHT } from './constants';
import { toBrowseCategoryIcon } from './mappers';

const ACTIVE_CATEGORY_CLASS = 'active-category';

const BrowseCategoryView: React.FC<React.PropsWithChildren<Props>> = ({ categories }) => {
  const scrollContainerRef = React.useRef<HTMLElement>(null);
  const scrollRef = React.useRef<HTMLLIElement>(null);
  const scrollToDomRef = (): void => {
    const scrollContainer = scrollContainerRef.current;
    const el = scrollRef.current;

    if (el && scrollContainer) {
      const elRect = el.getBoundingClientRect();
      const scrollRect = scrollContainer.getBoundingClientRect();

      if (elRect.left > scrollRect.width) {
        const scrollAmount = elRect.left - scrollRect.width + elRect.width;

        scrollContainer.scrollLeft = scrollAmount;
      }
    }
  };

  const isFreemiumMember = useIsFreemiumMember();

  const isSchedulePage = window.location.pathname.includes('schedule');

  const allScheduleURL = isSchedulePage
    ? toScheduleCategoryRoute(BrowseCategorySlug.All)
    : toLibraryCategoryRoute(BrowseCategorySlug.All);

  const freeClassesURL = toLibraryCategoryRoute(BrowseCategorySlug.Free);

  return (
    <Container>
      <ScrollContainer
        aria-label={useAria(Aria.FitnessDisciplines)}
        ref={scrollContainerRef}
        navHeight={BROWSE_CATEGORY_HEIGHT}
      >
        <CategoryList>
          <If condition={categories.length > 0}>
            <CategoryLinkView
              name={useClasses(Classes.All)}
              linkPath={allScheduleURL}
              scrollToDomRef={scrollToDomRef}
              scrollRef={scrollRef}
              slug={BrowseCategorySlug.All}
            />
          </If>
          <If condition={categories.length > 0 && isFreemiumMember && !isSchedulePage}>
            <CategoryLinkView
              name={useClasses(Classes.FreeBrowse)}
              linkPath={freeClassesURL}
              scrollToDomRef={scrollToDomRef}
              scrollRef={scrollRef}
              slug={BrowseCategorySlug.Free}
            />
          </If>
          {categories.map((category, index) => (
            <CategoryLinkView
              key={index}
              scrollToDomRef={scrollToDomRef}
              scrollRef={scrollRef}
              {...category}
            />
          ))}
        </CategoryList>
      </ScrollContainer>
    </Container>
  );
};

const CategoryLinkView: React.FC<React.PropsWithChildren<CategoryLink & ScrollRefs>> = ({
  name,
  slug,
  linkPath,
  scrollToDomRef,
  scrollRef,
}) => {
  React.useEffect(() => {
    if (window.location.pathname === linkPath) {
      scrollToDomRef();
    }
  });
  const allAriaLabel = useClasses(Classes.AllDisciplines);

  return (
    <CategoryLinkContainer ref={window.location.pathname === linkPath ? scrollRef : null}>
      <StyledLink
        data-test-id={`${slug}ClassCategory`}
        to={linkPath}
        activeClassName={ACTIVE_CATEGORY_CLASS}
        aria-label={slug === BrowseCategorySlug.All ? allAriaLabel : name}
      >
        <Icon>
          {React.createElement(
            toBrowseCategoryIcon(browseCategorySlugToCaesarMapper(slug)),
          )}
        </Icon>
        <Label data-test-id="classCategoryTitle">{name}</Label>
      </StyledLink>
    </CategoryLinkContainer>
  );
};

const Container = styled.div`
  position: relative;
`;

const ScrollContainer = styled.nav<{ navHeight: number }>`
  ${scrollableRowStyles}
  background-color: ${white};
  height: ${props => `${props.navHeight}px`};
  box-shadow: 0 3px 5px 0 ${transparentize(0.9, black)};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  ${media.tabletXLarge`
    justify-content: none;
  `}
`;

const CategoryList = styled.ul`
  padding: 0 5px;
  margin: auto;
  ${media.tablet`
    padding: 0 15px;
  `}
  ${media.desktop`
    padding: 0;
  `}
`;

const StyledLink = styled(NavLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
  color: ${slate1};
  fill: ${slate1};
  white-space: normal;
  &.${ACTIVE_CATEGORY_CLASS} {
    color: ${superRedA11y};
    fill: ${superRedA11y};
  }
`;

const Label = styled.div`
  ${label12}
  padding-top: ${spaces.xxxSmall}px;
  text-align: center;
  &::first-letter {
    text-transform: uppercase;
  }
`;

const Icon = styled.span`
  width: 32px;
  height: 32px;
`;

const hoverLinkStyles = css`
  border-radius: ${spaces.xxxSmall}px;
  ${hover`
    transition: background-color 250ms ease;
    background-color: ${gray1};
  `}
`;

const CategoryLinkContainer = styled.li`
  display: inline-block;
  padding: ${spaces.xSmall}px 0;
  & + & {
    margin-left: 15px;
  }
  ${media.tablet`
    & + & {
      margin-left: ${spaces.large}px;
    }
  `}
  ${media.desktop`
    ${hoverLinkStyles}
    padding: ${spaces.xSmall}px;
    & + & {
      margin-left: 14px;
    }
  `}
`;

type Props = {
  categories: CategoryLink[];
};

type ScrollRefs = {
  scrollRef: React.RefObject<HTMLLIElement>;
  scrollToDomRef: () => void;
};

export default BrowseCategoryView;
